import type { ReactNode } from 'react';
import clsx from 'clsx';
import { IconX } from './Icons';

/*
 * Shared UI kit. Conventions follow shadcn/ui:
 * - controls: h-9, rounded-md, border-input, 3px soft focus ring
 * - cards: rounded-xl, 1px border, hairline shadow
 * - badges: rounded-md, bordered, text-xs
 * - dialogs: title / body / right-aligned footer
 */

/* ---------- class presets ---------- */

export const card = 'bg-white border border-line rounded-xl shadow-[0_1px_2px_rgba(15,24,38,0.04)]';
export const cardHead = 'flex flex-wrap items-center justify-between gap-3 px-4 sm:px-6 py-4 border-b border-line-soft';
export const cardTitle = 'text-base font-semibold leading-none tracking-tight';
export const cardDesc = 'text-sm text-muted mt-1.5';
export const cardBody = 'p-4 sm:p-6';

export const th = 'h-10 px-3 sm:px-4 text-left align-middle text-[13px] font-medium text-muted whitespace-nowrap border-b border-line';
export const td = 'px-3 sm:px-4 py-3 align-middle text-sm whitespace-nowrap border-b border-line-soft group-last:border-b-0';
export const trHover = 'group transition-colors hover:bg-ground/70';
/** Secondary columns: hidden on phones, shown from md up. */
export const thMd = clsx(th, 'hidden md:table-cell');
export const tdMd = clsx(td, 'hidden md:table-cell');
/** Tertiary columns: hidden on the narrowest phones, shown from sm up. */
export const thSm = clsx(th, 'hidden sm:table-cell');
export const tdSm = clsx(td, 'hidden sm:table-cell');

const focusRing = 'focus-visible:outline-none focus-visible:border-primary focus-visible:ring-[3px] focus-visible:ring-primary/25';

export const input = clsx(
  'flex h-9 w-full rounded-md border border-line-strong bg-white px-3 py-1 text-sm text-ink shadow-[0_1px_2px_rgba(15,24,38,0.04)] transition-colors',
  'placeholder:text-[#9AA3B0] disabled:cursor-not-allowed disabled:opacity-60 read-only:bg-ground read-only:shadow-none',
  focusRing,
);
export const textarea = clsx(
  'flex min-h-[96px] w-full rounded-md border border-line-strong bg-white px-3 py-2 text-sm text-ink shadow-[0_1px_2px_rgba(15,24,38,0.04)] transition-colors',
  'placeholder:text-[#9AA3B0] disabled:cursor-not-allowed disabled:opacity-60',
  focusRing,
);
export const select = clsx(
  'h-9 rounded-md border border-line-strong bg-white pl-3 pr-8 text-sm text-ink shadow-[0_1px_2px_rgba(15,24,38,0.04)] transition-colors appearance-none bg-no-repeat',
  focusRing,
);
export const selectStyle = {
  backgroundImage:
    "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='%235F6B7A' stroke-width='2'><path d='M6 9l6 6 6-6'/></svg>\")",
  backgroundPosition: 'right 10px center',
} as const;
export const label = 'block text-sm font-medium leading-none mb-2';
export const hint = 'text-[13px] text-muted mt-1.5';

const btnBase = clsx(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors',
  'disabled:pointer-events-none disabled:opacity-50 h-9 px-4',
  'focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-primary/25',
);
export const btnPrimary = clsx(btnBase, 'bg-primary text-white shadow-[0_1px_2px_rgba(15,24,38,0.08)] hover:bg-primary-dark');
export const btnSecondary = clsx(btnBase, 'border border-line-strong bg-white text-ink shadow-[0_1px_2px_rgba(15,24,38,0.04)] hover:bg-ground');
export const btnGhost = clsx(btnBase, 'text-ink hover:bg-ground');
export const btnDanger = clsx(btnBase, 'border border-[#F3C2C2] bg-white text-bad hover:bg-bad-soft');
export const btnDangerSolid = clsx(btnBase, 'bg-bad text-white shadow-[0_1px_2px_rgba(15,24,38,0.08)] hover:bg-[#9A1E15]');
export const btnSm = '!h-8 !px-3 !text-[13px]';
export const btnLg = '!h-10 !px-5';

export const iconBtn =
  'inline-flex h-8 w-8 items-center justify-center rounded-md border border-line bg-white text-muted transition-colors hover:bg-ground hover:text-ink focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-primary/25';
export const iconBtnDanger =
  'inline-flex h-8 w-8 items-center justify-center rounded-md border border-line bg-white text-muted transition-colors hover:border-[#F3C2C2] hover:bg-bad-soft hover:text-bad focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-primary/25';
export const iconBtnOk =
  'inline-flex h-8 w-8 items-center justify-center rounded-md border border-line bg-white text-muted transition-colors hover:border-[#BFE3CC] hover:bg-ok-soft hover:text-ok focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-primary/25';
export const iconBtnGhost =
  'inline-flex h-7 w-7 items-center justify-center rounded-md text-muted transition-colors hover:bg-ground hover:text-ink';

/* ---------- layout ---------- */

export function Page({ children, narrow }: { children: ReactNode; narrow?: boolean }) {
  return (
    <div className={clsx('px-4 py-5 sm:px-6 sm:py-7 md:px-10 md:py-8 flex flex-col gap-5 sm:gap-6 [&>*]:min-w-0', narrow && 'max-w-3xl')}>
      {children}
    </div>
  );
}

export function PageHeader({ title, subtitle, back, actions }: {
  title: ReactNode;
  subtitle?: ReactNode;
  back?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-4">
      <div className="flex items-center gap-3 min-w-0">
        {back}
        <div className="flex flex-col gap-1 min-w-0">
          <h1 className="text-xl sm:text-2xl font-bold leading-tight tracking-tight truncate">{title}</h1>
          {subtitle && <p className="text-sm text-muted">{subtitle}</p>}
        </div>
      </div>
      {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
    </div>
  );
}

export function CardHeader({ title, description, actions, icon }: {
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  icon?: ReactNode;
}) {
  return (
    <div className={cardHead}>
      <div className="flex items-center gap-3 min-w-0">
        {icon && <span className="text-muted shrink-0">{icon}</span>}
        <div className="min-w-0">
          <h2 className={cardTitle}>{title}</h2>
          {description && <p className={cardDesc}>{description}</p>}
        </div>
      </div>
      {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
    </div>
  );
}

/* ---------- status ---------- */

const statusMap: Record<string, { label: string; cls: string; dot: string }> = {
  verified:  { label: '已確認', cls: 'border-[#BFE3CC] bg-ok-soft text-ok',      dot: 'bg-ok-dot' },
  pending:   { label: '待審核', cls: 'border-[#F3DDB0] bg-warn-soft text-warn',  dot: 'bg-warn-dot' },
  rejected:  { label: '已拒絕', cls: 'border-[#F3C2C2] bg-bad-soft text-bad',    dot: 'bg-bad-dot' },
  published: { label: '已發布', cls: 'border-[#BFE3CC] bg-ok-soft text-ok',      dot: 'bg-ok-dot' },
  draft:     { label: '草稿',   cls: 'border-line bg-line-soft text-muted',       dot: 'bg-[#9AA3B0]' },
  closed:    { label: '已關閉', cls: 'border-line bg-line-soft text-muted',       dot: 'bg-[#9AA3B0]' },
  cancelled: { label: '已取消', cls: 'border-line bg-line-soft text-muted',       dot: 'bg-[#9AA3B0]' },
};

export function statusLabel(status?: string) {
  return (status && statusMap[status]?.label) || status || '-';
}

export function Badge({ children, tone = 'neutral', className }: {
  children: ReactNode;
  tone?: 'neutral' | 'primary' | 'ok' | 'warn' | 'bad';
  className?: string;
}) {
  const cls = {
    neutral: 'border-line bg-line-soft text-muted',
    primary: 'border-[#C9D6E8] bg-primary-light text-primary',
    ok:      'border-[#BFE3CC] bg-ok-soft text-ok',
    warn:    'border-[#F3DDB0] bg-warn-soft text-warn',
    bad:     'border-[#F3C2C2] bg-bad-soft text-bad',
  }[tone];
  return (
    <span className={clsx('inline-flex items-center gap-1.5 rounded-md border px-2 py-0.5 text-xs font-medium whitespace-nowrap', cls, className)}>
      {children}
    </span>
  );
}

export function StatusChip({ status, className }: { status?: string; className?: string }) {
  const s = (status && statusMap[status]) || { label: status || '-', cls: 'border-line bg-line-soft text-muted', dot: 'bg-[#9AA3B0]' };
  return (
    <span className={clsx('inline-flex items-center gap-1.5 rounded-md border px-2 py-0.5 text-xs font-medium whitespace-nowrap', s.cls, className)}>
      <span aria-hidden="true" className={clsx('h-1.5 w-1.5 rounded-full', s.dot)} />
      {s.label}
    </span>
  );
}

/** A native select styled as a status badge, for in-table status changes. */
export function StatusSelect({ value, onChange, options, ariaLabel }: {
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
  ariaLabel?: string;
}) {
  const s = statusMap[value] || { cls: 'border-line bg-line-soft text-muted' };
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      aria-label={ariaLabel}
      className={clsx(
        'h-7 rounded-md border pl-2.5 pr-7 text-xs font-medium cursor-pointer appearance-none bg-no-repeat',
        'focus:outline-none focus:ring-[3px] focus:ring-primary/25',
        s.cls,
      )}
      style={{
        backgroundImage:
          "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2.5'><path d='M6 9l6 6 6-6'/></svg>\")",
        backgroundPosition: 'right 8px center',
      }}
    >
      {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  );
}

/* ---------- feedback ---------- */

export function Alert({ tone, children }: { tone: 'error' | 'success' | 'info'; children: ReactNode }) {
  const cls = {
    error:   'border-[#F3C2C2] bg-bad-soft text-[#8A1C1C]',
    success: 'border-[#BFE3CC] bg-ok-soft text-ok',
    info:    'border-[#C9D6E8] bg-primary-light text-primary-dark',
  }[tone];
  return (
    <div role={tone === 'error' ? 'alert' : 'status'} className={clsx('rounded-lg border px-4 py-3 text-sm leading-relaxed', cls)}>
      {children}
    </div>
  );
}

export function EmptyState({ icon, title, hint, action, className }: {
  icon?: ReactNode;
  title: string;
  hint?: string;
  action?: ReactNode;
  className?: string;
}) {
  return (
    <div className={clsx('flex flex-col items-center text-center gap-2 px-5 py-12', className)}>
      {icon && <span className="text-[#9AA3B0]">{icon}</span>}
      <div className="text-sm font-medium">{title}</div>
      {hint && <div className="text-[13px] text-muted max-w-sm">{hint}</div>}
      {action && <div className="mt-3">{action}</div>}
    </div>
  );
}

/* ---------- controls ---------- */

export function SegmentedControl<T extends string>({ value, onChange, options, ariaLabel }: {
  value: T;
  onChange: (v: T) => void;
  options: { value: T; label: string }[];
  ariaLabel: string;
}) {
  return (
    <div role="group" aria-label={ariaLabel} className="inline-flex h-9 items-center rounded-md bg-ground p-1 gap-0.5">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          onClick={() => onChange(o.value)}
          aria-pressed={value === o.value}
          className={clsx(
            'h-7 rounded-sm px-3 text-[13px] transition-colors whitespace-nowrap',
            value === o.value ? 'bg-white text-ink font-medium shadow-[0_1px_2px_rgba(15,24,38,0.08)]' : 'text-muted hover:text-ink',
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

export function Toggle({ checked, onChange, label: text, description }: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
  description?: string;
}) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-lg border border-line px-4 py-3">
      <div className="min-w-0">
        <div className="text-sm font-medium">{text}</div>
        {description && <div className="text-[13px] text-muted">{description}</div>}
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={text}
        onClick={() => onChange(!checked)}
        className={clsx('relative h-5 w-9 shrink-0 rounded-full transition-colors', checked ? 'bg-primary' : 'bg-[#CFD5DD]')}
      >
        <span className={clsx('absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform', checked ? 'left-[18px]' : 'left-0.5')} />
      </button>
    </div>
  );
}

/* ---------- dialog ---------- */

export function Modal({ title, description, onClose, children, footer, size = 'md' }: {
  title: string;
  description?: ReactNode;
  onClose: () => void;
  children?: ReactNode;
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg';
}) {
  const width = { sm: 'max-w-md', md: 'max-w-lg', lg: 'max-w-2xl' }[size];
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-[#0F1826]/60 p-0 sm:p-4" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={clsx('flex w-full max-h-[90vh] sm:max-h-[85vh] flex-col rounded-t-2xl sm:rounded-xl border border-line bg-white shadow-[0_20px_60px_rgba(15,24,38,0.25)] pb-[env(safe-area-inset-bottom)]', width)}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4 px-4 sm:px-6 pt-5 sm:pt-6 pb-4">
          <div className="min-w-0">
            <h3 className="text-lg font-semibold leading-none tracking-tight">{title}</h3>
            {description && <p className="mt-2 text-sm text-muted">{description}</p>}
          </div>
          <button type="button" onClick={onClose} aria-label="關閉" className={iconBtnGhost}>
            <IconX size={16} />
          </button>
        </div>
        {children && <div className="overflow-y-auto px-4 sm:px-6 pb-2">{children}</div>}
        {footer && <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 px-4 sm:px-6 pt-4 pb-5 sm:pb-6 [&>*]:w-full sm:[&>*]:w-auto">{footer}</div>}
      </div>
    </div>
  );
}
